import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import ModalPopup from "../../components/ModalPopup";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { api } from "../../utils/api";
import { COLORS } from "../../utils/colors";
import { getCurrentP1Region } from "../../utils/p1Region";
import { SmsStatusDisplay } from "./SmsStatusDisplay";
const {
  formatCommunicationsDateTime,
  normalizeSmsProvider,
} = require("./smsProviderDisplay");

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

function ProviderChip({ provider }) {
  const label = normalizeSmsProvider(provider);
  return (
    <Chip
      size="small"
      label={label}
      variant="outlined"
      color={provider === "ClickSend" ? "primary" : "default"}
    />
  );
}

const emptyPagination = { page: 0, pageSize: 25, total: 0, totalPages: 0 };

const CustomerCommunications = ({ customerId }) => {
  const p1Region = getCurrentP1Region();
  const formatSentDate = (value) =>
    formatCommunicationsDateTime(value, p1Region);

  const [commTab, setCommTab] = useState(0);
  const [smsLoading, setSmsLoading] = useState(true);
  const [emailLoading, setEmailLoading] = useState(true);
  const [smsError, setSmsError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sms, setSms] = useState([]);
  const [emails, setEmails] = useState([]);
  const [smsPagination, setSmsPagination] = useState(emptyPagination);
  const [emailPagination, setEmailPagination] = useState(emptyPagination);
  const [smsPage, setSmsPage] = useState(0);
  const [smsRowsPerPage, setSmsRowsPerPage] = useState(25);
  const [emailPage, setEmailPage] = useState(0);
  const [emailRowsPerPage, setEmailRowsPerPage] = useState(25);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDetailLoading, setEmailDetailLoading] = useState(false);
  const [emailDetailError, setEmailDetailError] = useState("");

  const loadSms = useCallback(async () => {
    if (!customerId) return;
    setSmsLoading(true);
    setSmsError("");
    try {
      const response = await api(
        `v1/customer/${customerId}/communications?channel=sms&page=${smsPage + 1}&pageSize=${smsRowsPerPage}`,
        "GET"
      );
      if (response.data?.success) {
        const data = response.data.data || {};
        setSms(data.sms || []);
        if (data.pagination?.sms) {
          const p = data.pagination.sms;
          setSmsPagination({
            page: Math.max((p.page || 1) - 1, 0),
            pageSize: p.pageSize || smsRowsPerPage,
            total: p.total ?? 0,
            totalPages: p.totalPages ?? 0,
          });
        }
      } else {
        setSmsError(response.data?.message || "Failed to load SMS history.");
      }
    } catch (err) {
      console.error("Error loading customer SMS:", err);
      setSmsError(
        err.response?.data?.message || "Unable to load SMS history."
      );
    } finally {
      setSmsLoading(false);
    }
  }, [customerId, smsPage, smsRowsPerPage]);

  const loadEmails = useCallback(async () => {
    if (!customerId) return;
    setEmailLoading(true);
    setEmailError("");
    try {
      const response = await api(
        `v1/customer/${customerId}/communications?channel=email&page=${emailPage + 1}&pageSize=${emailRowsPerPage}`,
        "GET"
      );
      if (response.data?.success) {
        const data = response.data.data || {};
        setEmails(data.emails || []);
        if (data.pagination?.emails) {
          const p = data.pagination.emails;
          setEmailPagination({
            page: Math.max((p.page || 1) - 1, 0),
            pageSize: p.pageSize || emailRowsPerPage,
            total: p.total ?? 0,
            totalPages: p.totalPages ?? 0,
          });
        }
      } else {
        setEmailError(response.data?.message || "Failed to load email history.");
      }
    } catch (err) {
      console.error("Error loading customer emails:", err);
      setEmailError(
        err.response?.data?.message || "Unable to load email history."
      );
    } finally {
      setEmailLoading(false);
    }
  }, [customerId, emailPage, emailRowsPerPage]);

  useEffect(() => {
    loadSms();
  }, [loadSms]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const handleViewEmail = async (row) => {
    setSelectedEmail({ ...row, html: null });
    setEmailDetailError("");
    setEmailDetailLoading(true);
    try {
      const response = await api(
        `v1/customer/${customerId}/communications/email/${row.id}`,
        "GET"
      );
      if (response.data?.success) {
        setSelectedEmail(response.data.data);
      } else {
        setEmailDetailError(
          response.data?.message || "Failed to load email content."
        );
      }
    } catch (err) {
      setEmailDetailError(
        err.response?.data?.message || "Unable to load email content."
      );
    } finally {
      setEmailDetailLoading(false);
    }
  };

  const handleCloseEmailModal = () => {
    setSelectedEmail(null);
    setEmailDetailError("");
    setEmailDetailLoading(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={commTab}
          onChange={(e, v) => setCommTab(v)}
          aria-label="customer communications tabs"
        >
          <Tab label={`SMS (${smsPagination.total})`} />
          <Tab label={`Emails (${emailPagination.total})`} />
        </Tabs>
      </Box>

      {smsLoading && commTab === 0 && <GlobalLoadingSpinner />}
      {emailLoading && commTab === 1 && <GlobalLoadingSpinner />}

      {!smsLoading && smsError && commTab === 0 && (
        <Typography color="error" variant="body2">
          {smsError}
        </Typography>
      )}
      {!emailLoading && emailError && commTab === 1 && (
        <Typography color="error" variant="body2">
          {emailError}
        </Typography>
      )}

      {commTab === 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 180 }}>Sent Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 110 }}>Provider</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 180 }}>Delivery Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!smsLoading && sms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No SMS records found for this customer (HOB or ClickSend).
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sms.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{formatSentDate(row.sentDate)}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {row.message}
                    </TableCell>
                    <TableCell>
                      <ProviderChip provider={row.provider || "HOB"} />
                    </TableCell>
                    <TableCell>
                      <SmsStatusDisplay
                        status={row.status}
                        statusDetail={row.statusDetail}
                      />
                    </TableCell>
                    <TableCell>{formatSentDate(row.deliveredDate)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={smsPagination.total}
            page={smsPage}
            onPageChange={(e, newPage) => setSmsPage(newPage)}
            rowsPerPage={smsRowsPerPage}
            onRowsPerPageChange={(e) => {
              setSmsRowsPerPage(parseInt(e.target.value, 10));
              setSmsPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </TableContainer>
      )}

      {commTab === 1 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 180 }}>Sent Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!emailLoading && emails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary">
                      No email records found for this customer in Hustle Outbound.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                emails.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{formatSentDate(row.sentDate)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 items-start">
                        <span className="text-sm font-medium">{row.subject}</span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewEmail(row)}
                          sx={{
                            borderColor: COLORS.priorityBlue.hex,
                            color: COLORS.priorityBlue.hex,
                          }}
                        >
                          View email
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status || "Sent"}
                        color={row.status === "Failed" ? "error" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={emailPagination.total}
            page={emailPage}
            onPageChange={(e, newPage) => setEmailPage(newPage)}
            rowsPerPage={emailRowsPerPage}
            onRowsPerPageChange={(e) => {
              setEmailRowsPerPage(parseInt(e.target.value, 10));
              setEmailPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </TableContainer>
      )}

      <ModalPopup
        showModal={!!selectedEmail}
        handleShowModal={handleCloseEmailModal}
        popupTitle={selectedEmail?.subject || "Email"}
        popupContent={
          selectedEmail ? (
            <div className="flex flex-col gap-3 max-h-[70vh] overflow-auto">
              <Typography variant="body2" color="text.secondary">
                Sent: {formatSentDate(selectedEmail.sentDate)}
              </Typography>
              {emailDetailLoading && (
                <div className="flex justify-center py-8">
                  <CircularProgress size={32} />
                </div>
              )}
              {emailDetailError && (
                <Typography color="error" variant="body2">
                  {emailDetailError}
                </Typography>
              )}
              {!emailDetailLoading && !emailDetailError && selectedEmail.html && (
                <iframe
                  title={selectedEmail.subject || "Email"}
                  sandbox="allow-popups allow-popups-to-escape-sandbox"
                  srcDoc={selectedEmail.html}
                  className="w-full border rounded bg-white"
                  style={{ height: "70vh" }}
                />
              )}
            </div>
          ) : null
        }
        cancelButtonText="Close"
        confirmButtonText=""
        confirmCallbackAction=""
      />
    </div>
  );
};

export default CustomerCommunications;
